'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { TradeDirectionMarker } from '@/components/market/identity/trade-direction-marker';
import { UserIdentity } from '@/components/profile/user-identity';
import { ThesisActions } from '@/components/social/debate/thesis-actions';
import { ThesisMarketAvatar } from '@/components/social/debate/thesis-market-avatar';
import { ThesisReplyThread } from '@/components/social/debate/thesis-reply-thread';
import { READ_ONLY_THESIS_REPLY_WRITE_ACCESS, type ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import { SolscanLink } from '@/components/social/shared/solscan-link';
import type { ThesisItem } from '@/components/social/types';
import { resolveProposalCategory } from '@/config/proposal-display';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useMarketDetailPrefetchIntent, useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FALLBACK_CATEGORY_EMOJI = '\u{1F4A1}';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Variant = 'debate' | 'portfolio';
interface ThesisCardProps {
  thesis: ThesisItem;
  isAuthenticated: boolean;
  userId: string | null;
  /** Shared market-level access controls writing only; threads stay public. */
  replyWriteAccess?: ThesisReplyWriteAccess;
  /** Opens the owning market's mobile trade sheet. */
  onOpenTradeForm?: () => void;
  /**
   * Discriminator between the two surfaces (spec 094):
   *
   * - `'debate'` (default) — rendered inside the market-detail Debate tab.
   *   Profile-picture avatar that routes to `/profile/{userId}`. The card body
   *   is not pressable; the viewer is already on the parent market. Replies
   *   expand inline.
   * - `'portfolio'` — rendered inside the portfolio Theses panel. Rounded
   *   card surface with an absolute-positioned `<Link>` overlay to the
   *   market's Debate tab anchored to this thesis. Avatar is a rounded-square
   *   market image with an outcome-colored status dot. Parent market title
   *   above each card uses the same typography as portfolio PositionRow market titles.
   *   Replies do not expand inline (the viewer is one tap away from the destination's full thread).
   */
  variant?: Variant;
  /** Cross-market feeds need a visible destination even with the debate presentation. */
  showMarketLink?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ThesisCard(t0) {
  const $ = _c(126);
  const {
    thesis,
    isAuthenticated,
    userId,
    replyWriteAccess: t1,
    onOpenTradeForm,
    variant: t2,
    showMarketLink: t3
  } = t0;
  const replyWriteAccess = t1 === undefined ? READ_ONLY_THESIS_REPLY_WRITE_ACCESS : t1;
  const variant = t2 === undefined ? "debate" : t2;
  const showMarketLink = t3 === undefined ? false : t3;
  const locale = useLocale();
  const [showReplies, setShowReplies] = useState(false);
  const [replyCount, setReplyCount] = useState(thesis.replyCount);
  const tTimeIntl = useTranslations("time");
  const tDebate = useTranslations("social.debate");
  const {
    formatD6
  } = useFormattedCurrency();
  let t4;
  if ($[0] !== tTimeIntl) {
    t4 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const tTime = t4;
  const t5 = thesis.outcomeLabel ?? "";
  let t6;
  if ($[2] !== t5 || $[3] !== thesis.outcomeIndex) {
    t6 = {
      title: t5,
      index: thesis.outcomeIndex
    };
    $[2] = t5;
    $[3] = thesis.outcomeIndex;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    hue
  } = useOutcomeColor(t6);
  const isBuy = thesis.tradeDirection === "buy";
  let t7;
  if ($[5] !== isBuy || $[6] !== tDebate) {
    t7 = isBuy ? tDebate("bought") : tDebate("sold");
    $[5] = isBuy;
    $[6] = tDebate;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const verb = t7;
  const outcomeLabel = thesis.outcomeLabel ?? "";
  let t8;
  if ($[8] !== formatD6 || $[9] !== thesis.tradeAmount) {
    t8 = formatD6(thesis.tradeAmount);
    $[8] = formatD6;
    $[9] = thesis.tradeAmount;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const tradeLineText = `${verb} ${outcomeLabel} for ${t8}`;
  const traderHref = `/portfolio/${thesis.trader.userId ?? ""}`;
  const marketHref = `/markets/${thesis.marketPubkey}`;
  const deepLinkHref = `/markets/${thesis.marketPubkey}?tab=debate&thesis=${thesis.signature}`;
  const traderIntent = useUserProfilePrefetchIntent(thesis.trader.userId);
  const marketIntent = useMarketDetailPrefetchIntent(thesis.marketPubkey);
  const deepLinkIntent = useIntentRoutePrefetch(deepLinkHref, marketIntent.onMouseEnter);
  const categoryEmoji = thesis.marketCategory.length > 0 ? resolveProposalCategory(thesis.marketCategory)?.emoji ?? FALLBACK_CATEGORY_EMOJI : FALLBACK_CATEGORY_EMOJI;
  let t9;
  if ($[11] !== replyWriteAccess || $[12] !== showReplies) {
    t9 = function handleToggleReplies() {
      if (!showReplies && replyWriteAccess.status === "ineligible") {
        replyWriteAccess.refresh();
      }
      setShowReplies(_temp);
    };
    $[11] = replyWriteAccess;
    $[12] = showReplies;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleToggleReplies = t9;
  let t10;
  if ($[14] !== thesis.trader) {
    t10 = <UserIdentity user={thesis.trader} size="md" nameClassName="text-foreground-secondary" handleClassName="text-body text-foreground-tertiary" />;
    $[14] = thesis.trader;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== traderHref || $[18] !== traderIntent) {
    t11 = <Link href={traderHref} prefetch={false} className="relative z-10 min-w-0 truncate hover:underline" {...traderIntent} onClick={_temp2}>{t10}</Link>;
    $[16] = t10;
    $[17] = traderHref;
    $[18] = traderIntent;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== locale || $[22] !== tTime || $[23] !== thesis.createdAt) {
    t13 = formatRelativeTime(thesis.createdAt, tTime, {
      locale
    });
    $[21] = locale;
    $[22] = tTime;
    $[23] = thesis.createdAt;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <span className="shrink-0 text-body text-foreground-tertiary">{t13}</span>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="shrink-0 text-body text-foreground-tertiary">·</span>;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== thesis.signature) {
    t16 = <SolscanLink signature={thesis.signature} variant="inline" className="relative z-10" />;
    $[28] = thesis.signature;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t11 || $[31] !== t14 || $[32] !== t16) {
    t17 = <div className="flex flex-wrap items-center gap-1">{t11}{t12}{t14}{t15}{t16}</div>;
    $[30] = t11;
    $[31] = t14;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const metadataRow = t17;
  const t18 = `text-outcome-${hue}-text`;
  let t19;
  if ($[34] !== t18) {
    t19 = cn("text-body tabular-nums", t18);
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t19 || $[37] !== tradeLineText) {
    t20 = <p className={t19}>{tradeLineText}</p>;
    $[36] = t19;
    $[37] = tradeLineText;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const tradeLine = t20;
  let t21;
  if ($[39] !== thesis.content) {
    t21 = <p className="text-body leading-snug">{thesis.content}</p>;
    $[39] = thesis.content;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const body = t21;
  if (variant === "debate") {
    const t22 = `thesis-${thesis.signature}`;
    const t23 = thesis.trader.avatarNsfw ?? false;
    const t24 = thesis.trader.avatarPreferences?.rasterise ?? false;
    let t25;
    if ($[41] !== t23 || $[42] !== t24 || $[43] !== thesis.trader.avatarUrl || $[44] !== thesis.trader.displayName || $[45] !== thesis.trader.userId) {
      t25 = <UserAvatar userId={thesis.trader.userId} displayName={thesis.trader.displayName} avatarUrl={thesis.trader.avatarUrl} nsfw={t23} rasterise={t24} size="md" />;
      $[41] = t23;
      $[42] = t24;
      $[43] = thesis.trader.avatarUrl;
      $[44] = thesis.trader.displayName;
      $[45] = thesis.trader.userId;
      $[46] = t25;
    } else {
      t25 = $[46];
    }
    let t26;
    if ($[47] !== thesis.tradeDirection) {
      t26 = <TradeDirectionMarker direction={thesis.tradeDirection} />;
      $[47] = thesis.tradeDirection;
      $[48] = t26;
    } else {
      t26 = $[48];
    }
    let t27;
    if ($[49] !== t25 || $[50] !== t26) {
      t27 = <span className="relative block size-10">{t25}{t26}</span>;
      $[49] = t25;
      $[50] = t26;
      $[51] = t27;
    } else {
      t27 = $[51];
    }
    let t28;
    if ($[52] !== t27 || $[53] !== traderHref || $[54] !== traderIntent) {
      t28 = <Link href={traderHref} prefetch={false} className="shrink-0" {...traderIntent}>{t27}</Link>;
      $[52] = t27;
      $[53] = traderHref;
      $[54] = traderIntent;
      $[55] = t28;
    } else {
      t28 = $[55];
    }
    let t29;
    if ($[56] !== deepLinkHref || $[57] !== deepLinkIntent || $[58] !== showMarketLink || $[59] !== thesis.marketTitle) {
      t29 = showMarketLink ? <Link href={deepLinkHref} prefetch={false} className="truncate text-body font-medium text-foreground-primary hover:underline" title={thesis.marketTitle} {...deepLinkIntent}>{thesis.marketTitle}</Link> : null;
      $[56] = deepLinkHref;
      $[57] = deepLinkIntent;
      $[58] = showMarketLink;
      $[59] = thesis.marketTitle;
      $[60] = t29;
    } else {
      t29 = $[60];
    }
    let t30;
    if ($[61] !== body || $[62] !== metadataRow || $[63] !== t29 || $[64] !== tradeLine) {
      t30 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t29}{metadataRow}{tradeLine}{body}</div>;
      $[61] = body;
      $[62] = metadataRow;
      $[63] = t29;
      $[64] = tradeLine;
      $[65] = t30;
    } else {
      t30 = $[65];
    }
    let t31;
    if ($[66] !== t28 || $[67] !== t30) {
      t31 = <div className="flex gap-3">{t28}{t30}</div>;
      $[66] = t28;
      $[67] = t30;
      $[68] = t31;
    } else {
      t31 = $[68];
    }
    let t32;
    if ($[69] !== handleToggleReplies || $[70] !== isAuthenticated || $[71] !== replyCount || $[72] !== thesis) {
      t32 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} onToggleReplies={handleToggleReplies} /></div>;
      $[69] = handleToggleReplies;
      $[70] = isAuthenticated;
      $[71] = replyCount;
      $[72] = thesis;
      $[73] = t32;
    } else {
      t32 = $[73];
    }
    let t33;
    if ($[74] !== isAuthenticated || $[75] !== onOpenTradeForm || $[76] !== replyWriteAccess || $[77] !== showReplies || $[78] !== thesis.marketPubkey || $[79] !== thesis.signature || $[80] !== userId) {
      t33 = showReplies ? <div className="contents md:block md:pl-[52px]"><ThesisReplyThread thesisSignature={thesis.signature} marketPubkey={thesis.marketPubkey} isAuthenticated={isAuthenticated} userId={userId} writeAccess={replyWriteAccess} onClose={() => setShowReplies(false)} {...onOpenTradeForm !== undefined ? {
          onOpenTradeForm
        } : {}} onReplyAdded={() => setReplyCount(_temp3)} onReplyRemoved={() => setReplyCount(_temp4)} /></div> : null;
      $[74] = isAuthenticated;
      $[75] = onOpenTradeForm;
      $[76] = replyWriteAccess;
      $[77] = showReplies;
      $[78] = thesis.marketPubkey;
      $[79] = thesis.signature;
      $[80] = userId;
      $[81] = t33;
    } else {
      t33 = $[81];
    }
    let t34;
    if ($[82] !== t22 || $[83] !== t31 || $[84] !== t32 || $[85] !== t33) {
      t34 = <div id={t22} className="flex flex-col gap-2 px-0 pt-3 pb-2">{t31}{t32}{t33}</div>;
      $[82] = t22;
      $[83] = t31;
      $[84] = t32;
      $[85] = t33;
      $[86] = t34;
    } else {
      t34 = $[86];
    }
    return t34;
  }
  const t22 = `thesis-${thesis.signature}`;
  let t23;
  if ($[87] !== deepLinkHref || $[88] !== deepLinkIntent || $[89] !== thesis.marketTitle) {
    t23 = <Link href={deepLinkHref} prefetch={false} aria-label={thesis.marketTitle} className="absolute inset-0 z-0 rounded-panel-row focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" {...deepLinkIntent} />;
    $[87] = deepLinkHref;
    $[88] = deepLinkIntent;
    $[89] = thesis.marketTitle;
    $[90] = t23;
  } else {
    t23 = $[90];
  }
  let t24;
  if ($[91] !== thesis.marketTitle) {
    t24 = <p className="truncate text-body font-medium text-foreground-primary" title={thesis.marketTitle}>{thesis.marketTitle}</p>;
    $[91] = thesis.marketTitle;
    $[92] = t24;
  } else {
    t24 = $[92];
  }
  let t25;
  if ($[93] !== categoryEmoji || $[94] !== hue || $[95] !== outcomeLabel || $[96] !== thesis.marketPubkey || $[97] !== thesis.outcomeImageNsfw || $[98] !== thesis.outcomeImageUrl || $[99] !== thesis.outcomeIndex) {
    t25 = <ThesisMarketAvatar marketPubkey={thesis.marketPubkey} outcomeIndex={thesis.outcomeIndex} outcomeTitle={outcomeLabel} outcomeImageUrl={thesis.outcomeImageUrl} outcomeImageNsfw={thesis.outcomeImageNsfw} categoryEmoji={categoryEmoji} hue={hue} size="md" />;
    $[93] = categoryEmoji;
    $[94] = hue;
    $[95] = outcomeLabel;
    $[96] = thesis.marketPubkey;
    $[97] = thesis.outcomeImageNsfw;
    $[98] = thesis.outcomeImageUrl;
    $[99] = thesis.outcomeIndex;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  let t26;
  if ($[101] !== marketHref || $[102] !== marketIntent || $[103] !== t25) {
    t26 = <Link href={marketHref} prefetch={false} className="relative z-10 shrink-0" {...marketIntent} onClick={_temp5}>{t25}</Link>;
    $[101] = marketHref;
    $[102] = marketIntent;
    $[103] = t25;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  let t27;
  if ($[105] !== body || $[106] !== metadataRow || $[107] !== tradeLine) {
    t27 = <div className="flex min-w-0 flex-1 flex-col gap-1">{tradeLine}{metadataRow}{body}</div>;
    $[105] = body;
    $[106] = metadataRow;
    $[107] = tradeLine;
    $[108] = t27;
  } else {
    t27 = $[108];
  }
  let t28;
  if ($[109] !== t26 || $[110] !== t27) {
    t28 = <div className="flex gap-3">{t26}{t27}</div>;
    $[109] = t26;
    $[110] = t27;
    $[111] = t28;
  } else {
    t28 = $[111];
  }
  let t29;
  if ($[112] !== isAuthenticated || $[113] !== replyCount || $[114] !== thesis) {
    t29 = <div className="pl-[52px]"><ThesisActions thesis={thesis} replyCount={replyCount} isAuthenticated={isAuthenticated} mode="display" /></div>;
    $[112] = isAuthenticated;
    $[113] = replyCount;
    $[114] = thesis;
    $[115] = t29;
  } else {
    t29 = $[115];
  }
  let t30;
  if ($[116] !== t28 || $[117] !== t29) {
    t30 = <div className="flex flex-col gap-2">{t28}{t29}</div>;
    $[116] = t28;
    $[117] = t29;
    $[118] = t30;
  } else {
    t30 = $[118];
  }
  let t31;
  if ($[119] !== t24 || $[120] !== t30) {
    t31 = <div className="flex flex-col gap-3">{t24}{t30}</div>;
    $[119] = t24;
    $[120] = t30;
    $[121] = t31;
  } else {
    t31 = $[121];
  }
  let t32;
  if ($[122] !== t22 || $[123] !== t23 || $[124] !== t31) {
    t32 = <div id={t22} className="relative rounded-panel-row border border-border-secondary p-3 transition-colors hover:bg-background-tertiary/80">{t23}{t31}</div>;
    $[122] = t22;
    $[123] = t23;
    $[124] = t31;
    $[125] = t32;
  } else {
    t32 = $[125];
  }
  return t32;
}
function _temp5(e_0) {
  e_0.stopPropagation();
}
function _temp4(c_0) {
  return Math.max(0, c_0 - 1);
}
function _temp3(c) {
  return c + 1;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(visible) {
  return !visible;
}